import { useEffect, useRef, useState } from "react";

export type DeviceHeadingStatus = "idle" | "requesting" | "granted" | "denied" | "unsupported";

// Bússola do aparelho (DeviceOrientation API) — diferente do `heading` da
// Geolocation API (que só existe enquanto o usuário está se deslocando).
// Client-side, nunca sai do navegador; só usado pra girar o personagem no
// mapa junto com o celular.
//
// iOS 13+ exige que `DeviceOrientationEvent.requestPermission()` seja
// chamado DENTRO de um gesto do usuário (clique/toque), de forma síncrona —
// não dá pra pedir automaticamente a partir de um efeito reagindo a estado
// (a permissão iOS falha silenciosamente se a chamada não estiver na mesma
// pilha de execução do clique). Por isso este hook expõe `requestPermission`
// pra quem chama disparar direto de um onClick; em navegadores que não
// exigem esse passo (Android/Chrome) o listener já liga sozinho quando
// `enabled` vira true.
export function useDeviceHeading(enabled: boolean) {
  const [status, setStatus] = useState<DeviceHeadingStatus>("idle");
  const [heading, setHeading] = useState<number | null>(null);
  const listenerAttachedRef = useRef(false);

  const attachListener = () => {
    if (listenerAttachedRef.current) return;
    listenerAttachedRef.current = true;
    const handleOrientation = (event: Event) => {
      const e = event as DeviceOrientationEvent & { webkitCompassHeading?: number };
      // iOS Safari: `webkitCompassHeading` já vem absoluto (0 = Norte,
      // sentido horário) — mais confiável que `alpha` nesses aparelhos.
      if (typeof e.webkitCompassHeading === "number") {
        setHeading(e.webkitCompassHeading);
        return;
      }
      if (e.alpha == null) return;
      // `alpha` é anti-horário a partir da orientação inicial do aparelho —
      // convertido pra heading estilo bússola (0 = Norte, sentido horário).
      setHeading((360 - e.alpha) % 360);
    };
    const eventName = "ondeviceorientationabsolute" in window ? "deviceorientationabsolute" : "deviceorientation";
    window.addEventListener(eventName, handleOrientation);
    (attachListener as unknown as { cleanup?: () => void }).cleanup = () =>
      window.removeEventListener(eventName, handleOrientation);
  };

  // Dispare isso direto de um onClick (ver comentário acima) — em
  // navegadores sem o requisito de permissão explícita, é seguro chamar de
  // qualquer lugar (inclusive do efeito abaixo).
  const requestPermission = async () => {
    const RequestPermissionFn = (
      window as unknown as {
        DeviceOrientationEvent?: { requestPermission?: () => Promise<"granted" | "denied"> };
      }
    ).DeviceOrientationEvent?.requestPermission;
    if (typeof RequestPermissionFn !== "function") {
      setStatus("granted");
      attachListener();
      return;
    }
    setStatus("requesting");
    try {
      const result = await RequestPermissionFn();
      if (result === "granted") {
        setStatus("granted");
        attachListener();
      } else {
        setStatus("denied");
      }
    } catch {
      setStatus("denied");
    }
  };

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined" || !("DeviceOrientationEvent" in window)) {
      setStatus("unsupported");
      return;
    }
    const RequestPermissionFn = (
      window as unknown as {
        DeviceOrientationEvent?: { requestPermission?: () => Promise<"granted" | "denied"> };
      }
    ).DeviceOrientationEvent?.requestPermission;
    // Só auto-liga quando o navegador NÃO exige o gesto explícito (Android/
    // a maioria dos Chrome) — no iOS 13+ fica esperando `requestPermission()`
    // ser chamado por fora, a partir de um clique real.
    if (typeof RequestPermissionFn !== "function") {
      setStatus("granted");
      attachListener();
    } else {
      setStatus((prev) => (prev === "granted" ? prev : "idle"));
    }
    return () => {
      (attachListener as unknown as { cleanup?: () => void }).cleanup?.();
      listenerAttachedRef.current = false;
    };
  }, [enabled]);

  return { status, heading, requestPermission };
}
